import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Divider,
  Badge,
  Icon,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Card,
  CardBody,
  useToast
} from '@chakra-ui/react';
import { 
  FaFileContract, 
  FaPhone, 
  FaEnvelope, 
  FaGlobe,
  FaCheckCircle,
  FaClock,
  FaLock,
  FaUsers,
  FaExclamationTriangle
} from 'react-icons/fa';

const TermsAndConditions = () => {
  const toast = useToast();
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('gray.800', 'gray.800');
  
  const handleContactClick = () => {
    toast({
      title: "Contact Information",
      description: "You can reach us at info@webasi.co or +94 76 066 8556",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
  };

  const ContactCard = () => (
    <Card bg={cardBg} shadow="md" borderRadius="xl" marginTop={'100px'}>
      <CardBody>
        <VStack spacing={4}>
          <Icon as={FaPhone} w={8} h={8} color={accentColor} />
          <Heading size="md" color={headingColor}>Need Help?</Heading>
          <Text textAlign="center" color={textColor}>
            Contact us for any questions about our terms or privacy practices
          </Text>
          <VStack spacing={2}>
            <HStack>
              <Icon as={FaEnvelope} color={accentColor} />
              <Link href="mailto:info@webasi.co" color={accentColor}>
                info@webasi.co
              </Link>
            </HStack>
            <HStack>
              <Icon as={FaPhone} color={accentColor} />
              <Text color={textColor}>+94 76 066 8556</Text>
            </HStack>
            <HStack>
              <Icon as={FaGlobe} color={accentColor} />
              <Link href="https://webasi.co" color={accentColor}>
                webasi.co
              </Link>
            </HStack>
          </VStack>
          <Button 
            colorScheme="orange" 
            size="sm" 
            onClick={handleContactClick}
            leftIcon={<FaPhone />}
          >
            Contact Us
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <div style={{width:"100%", height:'auto', overflow:"hidden"}} className="bodyWrapper">
      <Box width="100vw" minH="100vh" py={8}>
        <Container maxW="8xl">
          <SimpleGrid columns={[1, 1, 4]} spacing={8}>
            {/* Main Content */}
            <Box gridColumn={[1, 1, "1 / 4"]}>
              <Box borderRadius="xl" p={8}>
                <VStack spacing={8} align="stretch">
                  {/* Header */}
                  <Box textAlign="center" py={8}>
                    <Heading 
                      as='h2' 
                      size={{ base: '2xl', md: '3xl' }} 
                      marginBottom={{base:'50px',md:'100px'}} 
                      marginTop={{base:'50px',md:'100px'}} 
                      textAlign={'center'} 
                      fontFamily="EB Garamond" 
                      color={'black'} 
                      fontWeight={'400'}
                    >
                      Terms and Conditions
                    </Heading>
                    <Text 
                      fontSize={"20px"}
                      fontWeight={"400"}
                      fontFamily="EB Garamond"
                      color={"black"}
                      opacity={1}
                    >
                      Please read these terms carefully before using our services
                    </Text>
                  </Box>

                  <Accordion allowToggle>
                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          1. Introduction
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel>
                        <Text
                          fontSize={"20px"}
                          fontWeight={"400"}
                          fontFamily="EB Garamond"
                          color={"black"}
                        >
                          Welcome to WEBASI Web Development ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of our web development services and website located at webasi.co. By engaging our services or using our website, you agree to be bound by these Terms.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          2. Services
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={4}>
                          <Box>
                            <Heading size="sm" color={headingColor} mb={2}>2.1 Web Development Services</Heading>
                            <Text color={textColor} mb={3}>We provide custom web development services including but not limited to:</Text>
                            <List spacing={2}>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>UI/UX Design and Development</Text>
                              </ListItem>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>Frontend and Backend Development</Text>
                              </ListItem>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>Website Hosting Services</Text>
                              </ListItem>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>Domain Registration Services</Text>
                              </ListItem>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>WordPress and CMS Development</Text>
                              </ListItem>
                              <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                <Text as="span" color={textColor}>Technical Support and Maintenance</Text>
                              </ListItem>
                            </List>
                          </Box>
                          <Box>
                            <Heading size="sm" color={headingColor} mb={2}>2.2 Service Scope</Heading>
                            <Text color={textColor}>
                              Each project's specific scope, deliverables, and requirements will be outlined in a separate Service Contract signed by both parties.
                            </Text>
                          </Box>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          3. Payment Terms
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={4}>
                          <SimpleGrid columns={[1, 2]} spacing={4}>
                            <Card bg={useColorModeValue('blue.50', 'blue.900')} p={4}>
                              <Text fontWeight="bold" color={headingColor} mb={2}>Payment Structure</Text>
                              <List spacing={1}>
                                <ListItem color={textColor}>• Project fees in Service Contract</ListItem>
                                <ListItem color={textColor}>• Advance payment required</ListItem>
                                <ListItem color={textColor}>• Balance due on delivery</ListItem>
                                <ListItem color={textColor}>• Currency: Sri Lankan Rupees (LKR)</ListItem>
                              </List>
                            </Card>
                            <Card bg={useColorModeValue('green.50', 'green.900')} p={4}>
                              <Text fontWeight="bold" color={headingColor} mb={2}>Payment Methods</Text>
                              <List spacing={1}>
                                <ListItem color={textColor}>• Bank Transfer</ListItem>
                                <ListItem color={textColor}>• PayPal</ListItem>
                                <ListItem color={textColor}>• Other specified methods</ListItem>
                              </List>
                            </Card>
                          </SimpleGrid>
                          <Box bg={useColorModeValue('red.50', 'red.900')} p={4} borderRadius="md">
                            <HStack mb={2}>
                              <Icon as={FaExclamationTriangle} color="red.500" />
                              <Text fontWeight="bold" color={headingColor}>Late Payments</Text>
                            </HStack>
                            <Text color={textColor}>
                              Late payments may result in project delays or suspension of services until payment is received.
                            </Text>
                          </Box>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          4. Warranty and Support
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <SimpleGrid columns={[1, 2]} spacing={6}>
                          <Card bg={useColorModeValue('purple.50', 'purple.900')} p={4}>
                            <HStack mb={3}>
                              <Icon as={FaClock} color="purple.500" />
                              <Text fontWeight="bold" color={headingColor}>30-Day Warranty</Text>
                            </HStack>
                            <Text color={textColor}>
                              We warrant that deliverables will function as specified for 30 days after delivery.
                            </Text>
                          </Card>
                          <Card bg={useColorModeValue('orange.50', 'orange.900')} p={4}>
                            <HStack mb={3}>
                              <Icon as={FaUsers} color="orange.500" />
                              <Text fontWeight="bold" color={headingColor}>Free Support</Text>
                            </HStack>
                            <Text color={textColor}>
                              30 days of free technical support and minor adjustments included.
                            </Text>
                          </Card>
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          5. Intellectual Property Rights
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Text color={textColor} lineHeight="tall">
                          Upon full payment of all fees, intellectual property rights in the completed deliverables transfer to the Client. We retain rights to our pre-existing methodologies, tools, and general knowledge used in providing services.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                      <AccordionButton py={4}>
                        <Heading 
                          size="md" 
                          fontFamily="EB Garamond"
                          color={'black'}
                          fontWeight={'400'}
                        >
                          6. Confidentiality
                        </Heading>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Box bg={useColorModeValue('gray.100', 'gray.700')} p={4} borderRadius="md">
                          <HStack mb={3}>
                            <Icon as={FaLock} color={accentColor} />
                            <Text fontWeight="bold" color={headingColor}>2-Year Confidentiality</Text>
                          </HStack>
                          <Text color={textColor}>
                            We maintain strict confidentiality regarding all client information including business strategies, service credentials, and proprietary data for 2 years from disclosure date.
                          </Text>
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </Box>
            </Box>

            {/* Contact Sidebar */}
            <Box gridColumn={[1, 1, 4]} position="sticky" top={8} height="fit-content">
              <ContactCard />
            </Box>
          </SimpleGrid>

          {/* Footer */}
          <Box textAlign="center" py={8} mt={10}>
            <Text
              fontSize={"18px"}
              fontWeight={"400"}
              fontFamily="EB Garamond"
              color={"black"}
            >
              © {new Date().getFullYear()} WEBASI.co All rights reserved.
            </Text>
          </Box>
        </Container>
      </Box>
    </div>
  );
};

export default TermsAndConditions;